import Plugin from '../core/Plugin.js';
import * as SelectionUtils from '../SelectionUtils';

class CustomSnippetPlugin extends Plugin {
  constructor(editor, options = {}, snippets = {}) {
    super(editor, options);
    this.name = 'CustomSnippetPlugin';
    // Initialize with built-in snippets
    this.snippets = {
      ...this.getBuiltInSnippets(),
      ...snippets,
    };
  }

  /**
   * Get built-in snippets that come with the editor
   * @returns {Object} Object with snippet names as keys and HTML as values
   */
  getBuiltInSnippets() {
    return {
      'Note Box': this.createNoteBoxHTML(),
    };
  }

  /**
   * Create the HTML structure for a Note Box accordion
   * @returns {string} HTML string for the note box
   */
  createNoteBoxHTML() {
    return `<details style="display: block !important;" class="demme-note-box-accordion collapse collapse-plus bg-demme-orange-50 rounded-xl mb-4" data-notebox="true">
  <summary class="collapse-title text-xl font-normal text-demme-orange-700 flex items-center gap-2" style="font-family: 'Bree Serif', serif;">
    <span class="note-label" style="display: inline-block; text-align: left; margin-right: 8px;">Note</span>
    <i class="fal fa-notes" aria-hidden="true"></i>
  </summary>
  <div class="spa_content_wrapper">
    <div class="collapse-content">
      <div class="note-content pb-0" contenteditable="true">
        <p><br></p>
      </div>
    </div>
  </div>
</details>`;
  }

  getToolbarButtons() {
    return [
      {
        name: 'add-snippet',
        buttons: [
          {
            command: 'addSnippet',
            label: '<i class="fa-solid fa-hospital-user"></i>',
            title: 'Create New Custom Components',
          },
        ],
      },
      {
        name: 'manage-snippets',
        buttons: [
          {
            command: 'manageSnippets',
            label: '<i class="fa-solid fa-clipboard-user"></i>',
            title: 'Insert Custom Components',
          },
        ],
      },
    ];
  }

  executeCommand(command) {
    switch (command) {
      case 'addSnippet':
        this.showSnippetAdder();
        break;
      case 'manageSnippets':
        this.showSnippetPicker();
        break;
    }
  }

  getMetadata() {
    return {
      name: 'Custom Snippets',
      version: '1.0.0',
      description: 'Provides custom snippet insertion functionality',
    };
  }

  showSnippetPicker() {
    console.log('Showing snippet picker');
    // Save the current selection before showing modal
    const savedSelection = SelectionUtils.saveSelection();

    const snippetNames = Object.keys(this.snippets);
    const modal = this._createModal('Select a snippet to insert');
    const list = document.createElement('ul');
    list.style.listStyle = 'none';
    list.style.padding = '0';
    list.style.margin = '0';
    snippetNames.forEach((name, index) => {
      const item = document.createElement('li');
      item.style.margin = '10px 0';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.id = `custom-snippet-btn-${name.replace(/\s+/g, '-').toLowerCase()}`;
      btn.textContent = `${index + 1}. ${name}`;
      btn.style.padding = '8px 16px';
      btn.style.borderRadius = '4px';
      btn.style.border = '1px solid #007bff';
      btn.style.background = '#007bff';
      btn.style.color = '#fff';
      btn.style.fontWeight = '500';
      btn.style.fontSize = '14px';
      btn.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)';
      btn.style.cursor = 'pointer';
      btn.style.transition = 'background-color 0.2s, border-color 0.2s, transform 0.2s';
      btn.onmouseover = () => {
        btn.style.background = '#0056b3';
        btn.style.borderColor = '#0056b3';
        btn.style.transform = 'scale(1.04)';
      };
      btn.onmouseout = () => {
        btn.style.background = '#007bff';
        btn.style.borderColor = '#007bff';
        btn.style.transform = 'scale(1)';
      };
      btn.onclick = () => {
        // Focus editor first if richEditor exists
        if (this.editor.richEditor) {
          this.editor.richEditor.focus({ preventScroll: true });

          // Check if there's still a selection after focusing
          const selection = window.getSelection();
          if (!selection || selection.rangeCount === 0) {
            // No selection after focusing, restore saved selection if available
            if (savedSelection) {
              SelectionUtils.restoreSelection(savedSelection);
            }
          }
        }

        this.editor.insertHTML(this.snippets[name]);

        // Special handling for Note Box
        if (name === 'Note Box') {
          // Get the NoteBox plugin if it's loaded
          const noteBoxPlugin = this.editor.pluginManager?.get('noteBox');
          if (noteBoxPlugin) {
            // Ensure there's content after the note box
            noteBoxPlugin.ensureContentAfterNoteBox();
            // Focus the note box content area
            noteBoxPlugin.focusNoteBoxContent();
          }
          // Sync and save to history
          this.editor.syncToTextarea();
          this.editor.saveToHistory();
        }

        document.body.removeChild(modal);
      };
      item.appendChild(btn);
      list.appendChild(item);
    });
    modal.querySelector('.modal-content').appendChild(list);
    document.body.appendChild(modal);
  }

  showSnippetAdder() {
    console.log('Showing snippet adder');
    const modal = this._createModal('Add a new snippet');
    const form = document.createElement('form');
    form.style.display = 'flex';
    form.style.flexDirection = 'column';
    form.style.gap = '14px';
    form.style.marginTop = '10px';
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.placeholder = 'Snippet name';
    nameInput.required = true;
    nameInput.style.padding = '8px';
    nameInput.style.borderRadius = '4px';
    nameInput.style.border = '1px solid #ccc';
    nameInput.style.fontSize = '14px';
    nameInput.style.outline = 'none';
    nameInput.onfocus = () => (nameInput.style.borderColor = '#007bff');
    nameInput.onblur = () => (nameInput.style.borderColor = '#ccc');
    const contentInput = document.createElement('textarea');
    contentInput.placeholder = 'Snippet content';
    contentInput.required = true;
    contentInput.rows = 4;
    contentInput.style.padding = '8px';
    contentInput.style.borderRadius = '4px';
    contentInput.style.border = '1px solid #ccc';
    contentInput.style.fontSize = '14px';
    contentInput.style.outline = 'none';
    contentInput.onfocus = () => (contentInput.style.borderColor = '#007bff');
    contentInput.onblur = () => (contentInput.style.borderColor = '#ccc');
    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.textContent = 'Add Snippet';
    submitBtn.style.padding = '8px 16px';
    submitBtn.style.borderRadius = '4px';
    submitBtn.style.border = '1px solid #007bff';
    submitBtn.style.background = '#007bff';
    submitBtn.style.color = '#fff';
    submitBtn.style.fontWeight = '500';
    submitBtn.style.fontSize = '14px';
    submitBtn.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)';
    submitBtn.style.cursor = 'pointer';
    submitBtn.style.transition = 'background-color 0.2s, border-color 0.2s, transform 0.2s';
    submitBtn.onmouseover = () => {
      submitBtn.style.background = '#0056b3';
      submitBtn.style.borderColor = '#0056b3';
      submitBtn.style.transform = 'scale(1.04)';
    };
    submitBtn.onmouseout = () => {
      submitBtn.style.background = '#007bff';
      submitBtn.style.borderColor = '#007bff';
      submitBtn.style.transform = 'scale(1)';
    };
    form.appendChild(nameInput);
    form.appendChild(contentInput);
    form.appendChild(submitBtn);
    form.onsubmit = (e) => {
      e.preventDefault();
      const name = nameInput.value.trim();
      const content = contentInput.value;
      if (name && content) {
        this.registerSnippet(name, content);
        this._showModalMessage(modal, `Snippet "${name}" added.`);
      } else {
        this._showModalMessage(modal, 'Snippet name and content cannot be empty.');
      }
    };
    modal.querySelector('.modal-content').appendChild(form);
    document.body.appendChild(modal);
  }
  // Helper to create a modal dialog
  _createModal(title) {
    const modal = document.createElement('div');
    modal.className = 'custom-snippet-modal';
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.width = '100vw';
    modal.style.height = '100vh';
    modal.style.background = 'rgba(0,0,0,0.32)';
    modal.style.display = 'flex';
    modal.style.alignItems = 'center';
    modal.style.justifyContent = 'center';
    modal.style.zIndex = '9999';
    modal.style.transition = 'background 0.2s';
    const content = document.createElement('div');
    content.className = 'modal-content';
    content.style.background = '#fff';
    content.style.padding = '24px';
    content.style.borderRadius = '4px';
    content.style.minWidth = '320px';
    content.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
    content.style.position = 'relative';
    content.style.display = 'flex';
    content.style.flexDirection = 'column';
    content.style.alignItems = 'stretch';
    const header = document.createElement('h3');
    header.textContent = title;
    header.style.marginTop = '0';
    header.style.marginBottom = '18px';
    header.style.fontSize = '1.1em';
    header.style.fontWeight = 'bold';
    header.style.color = '#000';
    content.appendChild(header);
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.style.position = 'absolute';
    closeBtn.style.top = '16px';
    closeBtn.style.right = '20px';
    closeBtn.style.width = '28px';
    closeBtn.style.height = '28px';
    closeBtn.style.background = '#e6e7e8';
    closeBtn.style.border = 'none';
    closeBtn.style.borderRadius = '50%';
    closeBtn.style.fontSize = '1.3em';
    closeBtn.style.color = '#949598';
    closeBtn.style.cursor = 'pointer';
    closeBtn.style.boxShadow = '0 1px 4px rgba(0,0,0,0.08)';
    closeBtn.style.transition = 'background 0.2s, color 0.2s';
    closeBtn.onmouseover = () => {
      closeBtn.style.background = '#949598';
      closeBtn.style.color = '#fff';
    };
    closeBtn.onmouseout = () => {
      closeBtn.style.background = '#e6e7e8';
      closeBtn.style.color = '#949598';
    };
    closeBtn.onclick = () => document.body.removeChild(modal);
    content.appendChild(closeBtn);
    modal.appendChild(content);
    return modal;
  }

  // Helper to show a message in a modal
  _showModalMessage(modal, message) {
    let msg = modal.querySelector('.modal-message');
    if (!msg) {
      msg = document.createElement('div');
      msg.className = 'modal-message';
      msg.style.marginTop = '12px';
      msg.style.color = '#007b00';
      modal.querySelector('.modal-content').appendChild(msg);
    }
    msg.textContent = message;
    setTimeout(() => {
      if (modal.parentNode) document.body.removeChild(modal);
    }, 1200);
  }

  registerSnippet(name, content) {
    if (!this.snippets) {
      this.snippets = {};
    }
    this.snippets[name] = content;
  }

  getSnippet(name) {
    return this.snippets ? this.snippets[name] : null;
  }
}

export default CustomSnippetPlugin;
